import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Project, ProjectWithRelations } from '@rappider/rappider-sdk';
import { BreadcrumbOption, HeadingComponentConfig, AlertTypes, HeadingType, TextComponentConfig, SpinSize } from '@rappider/rappider-components/utils';
import { defaultToolbarTitleHeadingSize, PAGE_DEFINITIONS, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { StartApplication, StopApplication } from 'libs/project-source-code/src/lib/project-source-code-data-source/project-source-code.actions';
import { DeployManagementState } from 'libs/project/src/lib/states/deploy-management/deploy-management.reducer';
import { StoreKeys, SyncRemoteDataService } from 'libs/shared/src/lib/services/sync/sync-remote-data.service';
import { interval, Subject, Subscription, takeUntil } from 'rxjs';
import { selectSocialMediaSharingStatus } from 'libs/shared/src/lib/states/client-local-state/client-local-state.selectors';
import { ButtonTriggerService } from 'libs/project/src/lib/services/button-trigger.service';
import { NotificationService } from '@rappider/services';
import { CurrentDeployStatus } from '../../configs/current-deploy.enum';

@Component({
  selector: 'rappider-server-page',
  templateUrl: './server-page.component.html',
  styleUrls: ['./server-page.component.scss']
})
export class ServerPageComponent implements OnInit, OnDestroy {

  mainTitle: HeadingComponentConfig | undefined;
  title: string | string[] | BreadcrumbOption[] | undefined;
  displayToolbar = false;
  displayToolbarBackButton = false;
  activeProject: Project | ProjectWithRelations | undefined;
  subscriptions: Subscription[] = [];
  applicationInfo: any;
  backendInfo: any;
  frontendInfo: any;
  switchValue = false;
  switchState: string | undefined;
  applicationState: string | undefined;
  private pollingServer$ = new Subject<void>();

  isSocialMediaShared = false;
  loading = true;
  serverInfoAvailable = false;

  constructor(
    private store: Store<{ deployManagement: DeployManagementState; activeProject: Project, projectVersion: any }>,
    private syncRemoteDataService: SyncRemoteDataService,
    private buttonTriggerService: ButtonTriggerService,
    private notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.subscribeToData();
    interval(5000).pipe(
      takeUntil(this.pollingServer$)
    ).subscribe(() => {
      this.syncRemoteDataService.syncRemoteDataItemWithKey(this.activeProject?.id, StoreKeys.MonitorApplication);
    });
    this.buttonTriggerService.buttonClick$.subscribe((event) => {
      this.handleButtonTrigger(event);
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pollingServer$.next();
    this.pollingServer$.complete();
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToApplicationInfo(),
      this.subscribeToPowerButtonState(),
      this.subscribeToPowerButtonPosition(),
      this.subscribeToSocialMediaSharingStatus()
    ];
  }

  subscribeToApplicationInfo() {
    return this.store.select(state => state.deployManagement.applicationInfo).subscribe((applicationInfo: any) => {
      if (applicationInfo?.length) {
        this.serverInfoAvailable = true;
        this.backendInfo = applicationInfo?.find(appInfo => appInfo.Image.toLowerCase().includes('backend'));
        this.frontendInfo = applicationInfo?.find(appInfo => appInfo.Image.toLowerCase().includes('frontend'));
        // The 0th and 1st elements of the array represent either frontend or backend.
        let frontendState = applicationInfo[0]?.State;
        let backendState = applicationInfo[1]?.State;
        if (frontendState === CurrentDeployStatus.Exited || backendState === CurrentDeployStatus.Exited) {
          this.switchValue = false;
        } else {
          this.switchValue = true;
        }
        this.applicationState = (frontendState === CurrentDeployStatus.Running && backendState === CurrentDeployStatus.Running) ? CurrentDeployStatus.Running : (frontendState === CurrentDeployStatus.Exited || backendState === CurrentDeployStatus.Exited) ? CurrentDeployStatus.Exited : undefined;
      } else if (applicationInfo?.length === 0 || !applicationInfo) {
        this.serverInfoAvailable = false;
      }
    });
  }

  subscribeToActiveProject() {
    return this.store.select(state => state.activeProject.data).subscribe(activeProject => {
      if (activeProject) {
        this.activeProject = activeProject;
        this.setTitle();
        this.syncRemoteDataService.syncRemoteDataItemWithKey(activeProject?.id, StoreKeys.MonitorApplication);
      }
    });
  }

  subscribeToPowerButtonState() {
    return this.store.select(state => state.deployManagement.applicationState).subscribe((state: string) => {
      this.switchState = state;
    });
  }

  subscribeToPowerButtonPosition() {
    return this.store.select(state => state.deployManagement.switchPosition).subscribe((switchPosition: boolean) => {
      this.switchValue = switchPosition;
    });
  }

  subscribeToSocialMediaSharingStatus() {
    const loadingTimeout = setTimeout(() => {
      this.loading = false;
      this.setTitle();
    }, 3000);

    return this.store.select(selectSocialMediaSharingStatus).subscribe({
      next: (isShared: boolean) => {
        clearTimeout(loadingTimeout);
        this.isSocialMediaShared = isShared;
        this.loading = false;
        this.setTitle();
      },
      error: (error) => {
        clearTimeout(loadingTimeout);
        this.isSocialMediaShared = false;
        this.loading = false;
      }
    });
  }

  onSwitchToggled(switchValue: any) {
    if (switchValue) {
      this.store.dispatch(StartApplication());
    } else {
      this.store.dispatch(StopApplication());
    }
  }

  setTitle() {
    this.mainTitle = {
      content: PAGE_DEFINITIONS.PROJECTS.CHILDREN.PROJECTS_SERVER_MANAGEMENT.PAGE_TITLE,
      type: defaultToolbarTitleHeadingSize
    };
    this.title = [
      {
        label: this.activeProject?.name,
        redirectUrl: `${PATH_DEFINITIONS.PROJECTS.PROJECT_DETAIL_PATH}/${this.activeProject?.id}`
      },
      {
        label: PAGE_DEFINITIONS.PROJECTS.CHILDREN.PROJECTS_SERVER_MANAGEMENT.PAGE_TITLE
      }
    ];
  }

  handleButtonTrigger(event: { buttonType: string; data?: any }) {
    switch (event.buttonType) {
      case 'server-toggle-power':
        this.store.select(state => state.deployManagement?.applicationInfo)
          .subscribe((applicationInfo: any) => {
            if (!applicationInfo || applicationInfo.length === 0) {
              this.notificationService.createNotification('warning', 'Project Not Deployed', 'Please deploy your application first before managing server power state.');
              return;
            }

            const isServerRunning = applicationInfo.some((app: any) =>
              app.State === CurrentDeployStatus.Running || app.State === CurrentDeployStatus.Exited === false
            );

            if (isServerRunning) {
              this.onSwitchToggled(false);
            } else {
              this.onSwitchToggled(true);
            }
          }).unsubscribe();
        break;
    }
  }

}
